import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  TrendingUp,
  AccessTime,
  Label
} from '@mui/icons-material';
import { Video } from '../../types';
import { formatDate } from '../../utils/dates';
import { formatNumber } from '../../utils/money';

interface VideoDetailProps {
  video: Video;
}

const VideoDetail: React.FC<VideoDetailProps> = ({ video }) => {
  // Extraire l'ID YouTube de l'URL
  const extractYouTubeId = (url: string): string => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/;
    const match = url.match(regex);
    return match ? match[1] : '';
  };

  // Formater la durée en format lisible
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}min ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}min ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Formater les nombres avec séparateurs

  const videoId = extractYouTubeId(video.youtubeUrl);
  const watchPercentage = (video.averageWatchPercentage * 100).toFixed(1);

  return (
    <Box sx={{ py: 2 }}>
      {/* Aperçu de la vidéo YouTube */}
      <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 */ }}>
          {videoId ? (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title={video.title}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.200'
              }}
            >
              <Typography color="text.secondary">
                Aperçu vidéo non disponible
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Informations principales */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {video.title}
        </Typography>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <Chip
            label={video.subject}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={video.isActive ? 'Active' : 'Inactive'}
            color={video.isActive ? 'success' : 'default'}
            size="small"
          />
          <Chip
            icon={<AccessTime />}
            label={formatDuration(video.duration)}
            variant="outlined"
            size="small"
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body1" color="text.secondary" paragraph>
          {video.description}
        </Typography>
        {video.niveauScolaireId && (
          <Typography variant="body2" color="text.secondary">
            <strong>Niveau:</strong> {video.niveauScolaireId}
          </Typography>
        )}
      </Paper>

      {/* Statistiques */}
      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        Statistiques de la vidéo
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Visibility color="primary" />
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {formatNumber(video.views)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Nombre de vues
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" />
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  {formatNumber(video.completionCount)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Complétions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUp color="warning" />
                <Typography variant="h6" color="warning.main" fontWeight="bold">
                  {watchPercentage}%
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Taux de visionnage moyen
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={video.averageWatchPercentage * 100}
                  color={video.averageWatchPercentage > 0.7 ? 'success' : video.averageWatchPercentage > 0.4 ? 'warning' : 'error'}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tags */}
      {video.tags && video.tags.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Label color="action" />
            <Typography variant="h6" fontWeight="bold">
              Tags
            </Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {video.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Métadonnées */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Métadonnées
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Date de création:</strong>
            </Typography>
            <Typography variant="body1">
              {formatDate(video.createdAt, 'dayMonthLong')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Dernière mise à jour:</strong>
            </Typography>
            <Typography variant="body1">
              {formatDate(video.lastUpdated, 'dateTimeLong')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              <strong>URL YouTube:</strong>
            </Typography>
            <Typography variant="body2">
              <a
                href={video.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'none' }}
              >
                {video.youtubeUrl}
              </a>
            </Typography>
          </Grid>
          {video.thumbnailUrl && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Miniature:</strong>
              </Typography>
              <Box sx={{ maxWidth: 300 }}>
                <img
                  src={video.thumbnailUrl}
                  alt={`Miniature de ${video.title}`}
                  style={{ width: '100%', borderRadius: 8 }}
                  onError={(event) => {
                    (event.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default VideoDetail;
